import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRequest } from '@/hooks';
import { MISSION_API_VERSION_HEADER, normalizeQuestLabel } from '@/pages/missions/utils';

function normalizeMissionQuests(missions: Mission[]) {
  return missions.map((mission) => ({
    ...mission,
    quests: Array.isArray(mission.quests) ? mission.quests.map(normalizeQuestLabel) : mission.quests
  }));
}

function extractMissions(payload: unknown): Mission[] | undefined {
  if (Array.isArray(payload)) {
    return normalizeMissionQuests(payload);
  }

  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const response = payload as Record<string, unknown>;

  if (Array.isArray(response.missions)) {
    return normalizeMissionQuests(response.missions as Mission[]);
  }

  if (Array.isArray(response.data)) {
    return normalizeMissionQuests(response.data as Mission[]);
  }

  if (response.data && typeof response.data === 'object') {
    const nestedData = response.data as Record<string, unknown>;
    if (Array.isArray(nestedData.missions)) {
      return normalizeMissionQuests(nestedData.missions as Mission[]);
    }
  }

  return undefined;
}

export default function useMission() {
  const { appId, scenarioId, missionId } = useParams<AppParam>();

  const {
    data: missionResponse,
    isValidating,
    mutate
  } = useRequest<unknown>(
    `/apps/${appId}/scenarios/${scenarioId}/missions`,
    {},
    MISSION_API_VERSION_HEADER
  );

  const missions = useMemo(() => extractMissions(missionResponse), [missionResponse]);
  const missionIndex = missionId
    ? missions?.findIndex((mission) => mission.id.toString() === missionId)
    : -1;
  const mission =
    missions && typeof missionIndex === 'number' && missionIndex >= 0
      ? missions[missionIndex]
      : undefined;

  return { missions, mission, missionIndex, isValidating, mutate };
}
